import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import Logo from '../components/Logo';

const Terms = () => {
  const navigate = useNavigate();
  
  return (
    <div className="relative bg-black text-white min-h-screen">
      {/* Animated Gradient Background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div 
          className="absolute w-[800px] h-[800px] rounded-full blur-[120px] opacity-20 animate-float"
          style={{
            background: 'radial-gradient(circle, rgba(59, 130, 246, 0.4) 0%, transparent 70%)',
            top: '10%',
            left: '10%',
            animation: 'float 20s ease-in-out infinite'
          }}
        />
        <div 
          className="absolute w-[600px] h-[600px] rounded-full blur-[100px] opacity-20 animate-float-delayed"
          style={{
            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.3) 0%, transparent 70%)',
            bottom: '20%',
            right: '10%',
            animation: 'float 25s ease-in-out infinite 5s'
          }}
        />
      </div>

      {/* Header */}
      <header className="relative z-50 border-b border-white/10 bg-black/50 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-6 flex items-center justify-between">
          <Logo height={100} />
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            className="border-white/30 bg-white/5 hover:bg-white/10 hover:border-white/50 text-white rounded-full px-6 py-3 text-sm transition-all duration-300"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="relative z-10 max-w-4xl mx-auto px-6 lg:px-12 py-16">
        <div className="prose prose-invert prose-lg max-w-none">
          <h1 className="text-5xl sm:text-6xl font-extralight tracking-tighter mb-4 glow-text-section">
            Glaciert AI Terms of Service
          </h1>
          
          <p className="text-gray-400 mb-12">
            <strong>Effective Date: November 23, 2025</strong><br />
            <strong>Last Updated: November 23, 2025</strong>
          </p>

          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8 mb-12">
            <p className="text-xl font-light leading-relaxed">
              <strong>BY ACCESSING, USING, OR REGISTERING FOR ANY GLACIERT AI WEBSITE, APPLICATION, API, MODEL, SERVICE, SOFTWARE, OR RELATED OFFERING (collectively, the "Service"), YOU ARE ENTERING INTO A LEGALLY BINDING CONTRACT WITH GLACIERT AI, INC. AND YOU IRREVOCABLY AGREE TO ALL TERMS BELOW. IF YOU DO NOT AGREE, YOU ARE NOT AUTHORIZED TO USE THE SERVICE.</strong>
            </p>
          </div>

          <section className="mb-12">
            <h2 className="text-3xl font-light tracking-tight mb-6 mt-12">1. Unilateral Modification of These Terms</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              We reserve the absolute, unrestricted right to modify, amend, replace, or supplement these Terms at any time, with or without notice to you. The current version will be posted at https://glaciert.ai/terms with the "Last Updated" date. Your continued use of the Service after any change constitutes your irrevocable acceptance of the modified Terms. You are solely responsible for reviewing this document regularly.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-light tracking-tight mb-6 mt-12">2. Nature of the Service – Provided Strictly "As-Is"</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong>2.1</strong> The Service consists of experimental artificial intelligence models and related tools.
            </p>
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong>2.2</strong> All outputs (text, code, images, audio, video, or otherwise) are probabilistic, non-deterministic, and may be inaccurate, incomplete, biased, offensive, harmful, illegal, or dangerous.
            </p>
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong>2.3</strong> You acknowledge that AI technology is inherently unreliable and unsuitable for any high-stakes, safety-critical, or regulated use.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-light tracking-tight mb-6 mt-12">3. Complete Disclaimer of Warranties</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              THE SERVICE IS PROVIDED "AS IS," "WITH ALL FAULTS," AND "AS AVAILABLE" WITHOUT ANY WARRANTY OF ANY KIND. GLACIERT AND ITS LICENSORS EXPRESSLY DISCLAIM ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR ANY PURPOSE, NON-INFRINGEMENT, ACCURACY, RELIABILITY, SECURITY, AVAILABILITY, OR FREEDOM FROM ERRORS OR MALWARE. NO ORAL OR WRITTEN INFORMATION PROVIDED BY GLACIERT OR ITS REPRESENTATIVES SHALL CREATE ANY WARRANTY.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-light tracking-tight mb-6 mt-12">4. Limitation of Liability</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW:
            </p>
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong>4.1</strong> GLACIERT SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES, INCLUDING LOSS OF PROFITS, DATA, GOODWILL, OR ANY PERSONAL INJURY OR DEATH, EVEN IF ADVISED OF THE POSSIBILITY.
            </p>
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong>4.2</strong> OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING FROM THESE TERMS OR THE SERVICE SHALL NOT EXCEED THE GREATER OF (A) ONE HUNDRED U.S. DOLLARS ($100) OR (B) THE AMOUNT YOU PAID US IN THE THREE (3) MONTHS PRIOR TO THE CLAIM.
            </p>
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong>4.3</strong> These limitations apply regardless of the legal theory (contract, tort, etc.) and even if any remedy fails of its essential purpose.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-light tracking-tight mb-6 mt-12">5. Your Full Indemnification Obligation</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              You agree to defend, indemnify, and hold harmless Glaciert AI, Inc., its affiliates, officers, directors, employees, agents, and licensors from any claim, demand, loss, liability, damage, or expense (including reasonable attorneys' fees) arising from:
            </p>
            <ul className="list-disc list-inside text-gray-300 leading-relaxed space-y-2 ml-4">
              <li>Your use or misuse of the Service</li>
              <li>Any Input or Output</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any law or third-party right</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-light tracking-tight mb-6 mt-12">6. Strict One-Account-Per-User Rule (Anti-Abuse)</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong>6.1</strong> You may create and maintain only ONE (1) account per natural person or legal entity.
            </p>
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong>6.2</strong> Creating, using, or maintaining multiple accounts (including alts, throwaways, proxies, or shared accounts) is strictly prohibited.
            </p>
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong>6.3</strong> Violation triggers immediate, permanent termination of all accounts, forfeiture of credits/data, permanent ban, and no refund or appeal.
            </p>
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong>6.4</strong> We monitor via IP, device fingerprint, payment methods, usage patterns, and any other means. Evasion attempts themselves violate these Terms.
            </p>
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong>6.5</strong> Enterprise multi-user access is allowed only via a separate written agreement.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-light tracking-tight mb-6 mt-12">7. User Content & Outputs</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong>7.1</strong> You are solely responsible for all Inputs and Outputs.
            </p>
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong>7.2</strong> You grant Glaciert an irrevocable, perpetual, worldwide, royalty-free license to use, store, modify, and train on all Inputs and Outputs for any purpose.
            </p>
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong>7.3</strong> We may delete or block any Input or Output at any time without notice.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-light tracking-tight mb-6 mt-12">8. Prohibited Conduct (Non-Exhaustive)</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              You may not:
            </p>
            <ul className="list-disc list-inside text-gray-300 leading-relaxed space-y-2 ml-4">
              <li>Violate any law</li>
              <li>Generate illegal, harmful, or abusive content</li>
              <li>Impersonate others</li>
              <li>Reverse engineer, extract model weights, or create competing services</li>
              <li>Overload, attack, or disrupt the Service</li>
              <li>Use the Service in high-risk activities (medical, aviation, weapons, etc.)</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-light tracking-tight mb-6 mt-12">9. Termination & Suspension</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              We may suspend or terminate your access instantly, without notice or liability, for any reason or no reason.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-light tracking-tight mb-6 mt-12">10. Governing Law, Venue, No Jury, No Class Actions</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong>10.1</strong> Governed by the laws of the State of Delaware, excluding conflict-of-law rules.
            </p>
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong>10.2</strong> Exclusive venue: state and federal courts in Wilmington, Delaware.
            </p>
            <p className="text-gray-300 leading-relaxed mb-4">
              <strong>10.3</strong> You waive all rights to jury trial and class/collective actions.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-light tracking-tight mb-6 mt-12">11. Miscellaneous</h2>
            <ul className="list-disc list-inside text-gray-300 leading-relaxed space-y-2 ml-4">
              <li>Entire agreement; severability</li>
              <li>No third-party beneficiaries</li>
              <li>No waiver unless in writing signed by us</li>
              <li>We may assign these Terms freely; you may not</li>
              <li>Force majeure excuses our non-performance</li>
            </ul>
          </section>

          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-8 mt-12">
            <p className="text-gray-300 leading-relaxed">
              <strong>Glaciert AI, Inc.</strong><br />
              1209 Orange Street, Wilmington, DE 19801, USA<br />
              <a href="https://glaciert.ai/terms" className="text-blue-400 hover:text-blue-300 underline">https://glaciert.ai/terms</a>
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Terms;

